import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type TelegramAdmin,
  type TelegramTest,
} from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";

/**
 * Administración del bot de Telegram (solo admin): guardar/rotar el token
 * (se valida con Telegram, se cifra y se registra el webhook en el mismo
 * paso), probar y eliminar. El token JAMÁS se muestra — solo el @username.
 */
export default function TelegramAdminPanel() {
  const toast = useToast();
  const [estado, setEstado] = useState<TelegramAdmin | null>(null);
  const [token, setToken] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [confirmBorrar, setConfirmBorrar] = useState(false);

  async function cargar() {
    try {
      setEstado(await api.get<TelegramAdmin>("/api/telegram/admin"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar el estado de Telegram", msg);
    }
  }

  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function guardar() {
    if (!token.trim()) return;
    setGuardando(true);
    try {
      const r = await api.put<TelegramAdmin>("/api/telegram/admin", {
        token: token.trim(),
      });
      setEstado(r);
      setToken("");
      toast.success(
        `Bot @${r.bot_username} configurado`,
        "Token cifrado y webhook registrado. Los usuarios ya pueden vincularse desde su Perfil.",
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude guardar el token", msg);
    } finally {
      setGuardando(false);
    }
  }

  async function probar() {
    setProbando(true);
    try {
      const r = await api.post<TelegramTest>("/api/telegram/admin/test", {});
      if (r.ok) {
        toast.success(
          r.detalle,
          r.mensaje_enviado ? "Te mandé un mensaje de prueba al chat." : undefined,
        );
      } else {
        toast.error("La prueba falló", r.detalle);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude probar", msg);
    } finally {
      setProbando(false);
    }
  }

  async function borrar() {
    try {
      await api.del("/api/telegram/admin");
      setConfirmBorrar(false);
      await cargar();
      toast.success("Token eliminado", "El bot quedó desconectado de ArkTickets.");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude eliminar", msg);
    }
  }

  return (
    // El encabezado y la explicación los pone el marco de la sección
    // (components/configuracion/secciones.tsx). Aquí sólo van los controles.
    <div className="card space-y-3 max-w-2xl">
      {estado && (
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide">
          <span
            className={`px-2 py-0.5 rounded border ${
              estado.configurado
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
            }`}
          >
            {estado.configurado
              ? `configurado · @${estado.bot_username ?? "?"}`
              : "sin configurar"}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Token del bot (lo da @BotFather)"
          autoComplete="off"
          className="input flex-1"
        />
        <button
          onClick={() => void guardar()}
          disabled={guardando || !token.trim()}
          className="btn-primary text-sm w-full sm:w-auto disabled:opacity-50"
        >
          {guardando ? "Guardando…" : "Guardar"}
        </button>
      </div>

      {estado?.configurado && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => void probar()}
            disabled={probando}
            className="btn-ghost text-sm w-full sm:w-auto disabled:opacity-50"
          >
            {probando ? "Probando…" : "Probar"}
          </button>
          <button
            onClick={() => setConfirmBorrar(true)}
            className="btn-ghost text-sm w-full sm:w-auto text-red-400/80 hover:text-red-300"
          >
            Eliminar token
          </button>
        </div>
      )}

      {confirmBorrar && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmBorrar(false)}
          title="Eliminar el token del bot"
          description="Se desconecta el webhook y dejan de salir avisos por Telegram hasta que cargues un token nuevo. Las vinculaciones de los usuarios se conservan."
          confirmLabel="Eliminar"
          variant="danger"
          onConfirm={() => void borrar()}
        />
      )}
    </div>
  );
}
